import type { CustomizationOptions } from "@tiendanube/nube-sdk-internal-types";

/**
 * The runtime type each option must arrive with, derived from
 * `CustomizationOptions` so the two cannot disagree: adding an option without
 * an entry here, or giving it the wrong kind, is a compile error.
 */
type OptionKinds = {
	[K in keyof CustomizationOptions]: CustomizationOptions[K] extends boolean
		? "boolean"
		: "string";
};

/**
 * Every option name the catalog knows, with the `typeof` its value must have.
 * Options come off the worker wire, so this is what they are checked against
 * before any handler sees them.
 */
const OPTION_KINDS: OptionKinds = {
	text: "string",
	fontColor: "string",
	backgroundColor: "string",
	borderRadius: "string",
	hidden: "string",
	disabled: "boolean",
};

/**
 * Why option `name` with `value` cannot reach a handler, or `null` when it
 * can.
 *
 * - an **unknown name** is refused rather than ignored: an app built against
 *   newer types, running on this SDK, would otherwise be told `ok: true`
 *   about an option nothing applied. Refusing it is what makes a new option
 *   feature-detectable, the way `unknown_target` makes a new target;
 * - a **mistyped value** is malformed input, kept out of `validate` and
 *   `operation`, which would otherwise coerce it into the DOM or throw on it.
 */
export function optionRefusal(name: string, value: unknown): string | null {
	if (!Object.prototype.hasOwnProperty.call(OPTION_KINDS, name)) {
		return `unknown option "${name}"`;
	}
	const kind = OPTION_KINDS[name as keyof OptionKinds];
	// Compared against literals, not `typeof value !== kind`: only a literal
	// lets the linter (and a reader) see that every branch is a real type.
	const matches =
		kind === "boolean" ? typeof value === "boolean" : typeof value === "string";
	if (!matches) return `option "${name}" must be a ${kind}`;
	return null;
}
